import * as React from 'react';
import { ChevronDown } from 'lucide-react';

import { cn, formatCompactCurrency } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Les chiffres essentiels d'un écran, en pastilles d'une ligne.
 *
 * Règle de la maison: les statistiques ne prennent pas la page. Chaque chiffre
 * est une pastille horizontale (la valeur, puis son libellé à côté, environ
 * 28 px de haut) posée dans le coin libre de l'en-tête. Par défaut elles se
 * rangent sur deux rangées au plus (2×2 pour quatre cases, le bouton
 * « Plus d'indicateurs » comptant pour une, soit environ 64 px de haut);
 * `disposition="ligne"` les met côte à côte sur une seule rangée, qui défile
 * sur petit écran au lieu de passer à la ligne; `disposition="pile"` les
 * empile en colonne dès 1024 px (toutes visibles, de même largeur), ce qui
 * libère la largeur du titre et s'aligne sur la hauteur de boutons empilés;
 * `disposition="libre"` (hors en-tête, dans une rangée de boutons) les garde
 * toutes visibles côte à côte et les fait passer à la ligne quand la place
 * manque, sans « Plus » ni défilement horizontal.
 *
 * Une statistique: `{ label, valeur, unite?, ton?, aide?, onClick? }`.
 *  - `ton`: 'neutre' | 'attention' | 'critique' | 'positif' (le chiffre se
 *    colore, le libellé dit toujours le sens: la couleur n'est jamais seule);
 *  - `aide`: phrase affichée au survol;
 *  - `onClick`: la pastille devient un bouton (aller voir le détail).
 * `plus` accueille un contenu secondaire, ouvert dans une fenêtre flottante
 * depuis la pastille « Plus d'indicateurs ».
 *
 * Règles anti-orphelin (jamais une case seule qui dépasse):
 *  - « Plus » n'apparaît pas pour UN SEUL chiffre masqué: il s'affiche à sa place;
 *  - nombre de cases impair dès 1440 px: avec « Plus », il devient une petite
 *    pastille collée au dernier chiffre; sans « Plus », le dernier chiffre
 *    s'étend sur les deux rangées.
 *
 * Largeurs (sans JavaScript, par classes responsives):
 *  - 1440 px et plus: toutes les pastilles (4 au plus), sur deux rangées;
 *  - de 1024 à 1439 px: UNE seule rangée. Seules la pastille la plus
 *    importantes (2 dès 1280 px, 3 dès 1366 px) restent visibles; les autres passent dans
 *    la fenêtre de la pastille « Plus » (qui reprend aussi `plus`). Les longs
 *    montants en FCFA s'abrègent (« 163 M FCFA », valeur exacte au survol);
 *  - sous 1024 px: toutes les pastilles sur une rangée qui défile.
 * `priorite` (1 = toujours visible, plus grand = plus tôt masqué) est
 * facultative sur chaque statistique: à défaut, l'ordre du tableau fait foi.
 */
const TONS = {
  neutre: 'text-foreground',
  attention: 'text-flame-ink',
  critique: 'text-destructive',
  positif: 'text-success',
};

/**
 * Pastilles gardées visibles (les autres vont dans « Plus »): 1 de 1024 à
 * 1279 px, 2 de 1280 à 1365 px, 3 de 1366 à 1439 px, toutes dès 1440 px.
 */
export const PASTILLES_ETROIT = 1;
export const PASTILLES_MOYEN = 2;
export const PASTILLES_LARGE = 3;

/**
 * Rang d'affichage de chaque statistique (0 = la plus importante): tri stable
 * sur `priorite`, à défaut l'ordre du tableau.
 */
export const rangsDePriorite = (liste) => {
  const ordre = liste
    .map((stat, index) => ({
      index,
      p: Number.isFinite(Number(stat.priorite)) && stat.priorite !== null
        ? Number(stat.priorite)
        : index + 1,
    }))
    .sort((a, b) => a.p - b.p || a.index - b.index);
  const rangs = new Array(liste.length).fill(0);
  ordre.forEach(({ index }, rang) => {
    rangs[index] = rang;
  });
  return rangs;
};

/** Montant en FCFA affiché en toutes lettres: « 163 144 000 FCFA » -> nombre. */
const montantFcfa = (valeur) => {
  if (typeof valeur !== 'string') return null;
  const m = valeur.match(/^\s*(-?)([\d\s\u00a0\u202f]+)\s*FCFA\s*$/);
  if (!m) return null;
  const nombre = Number(m[2].replace(/[\s\u00a0\u202f]/g, ''));
  if (!Number.isFinite(nombre)) return null;
  return m[1] ? -nombre : nombre;
};

/** Version abrégée d'une valeur (à partir du million), sinon `null`. */
export const valeurCompacte = (stat) => {
  if (stat.valeurCompacte) return stat.valeurCompacte;
  const nombre = montantFcfa(stat.valeur);
  if (nombre === null || Math.abs(nombre) < 1_000_000) return null;
  return formatCompactCurrency(nombre);
};

const affiche = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return '—';
  return typeof valeur === 'number' ? valeur.toLocaleString('fr-FR') : valeur;
};

const GABARIT =
  'inline-flex h-7 min-w-0 max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border/70 bg-card px-2 text-left leading-none';
const INTERACTIF =
  'transition-colors hover:border-primary/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

// Les trois plages où l'en-tête n'a qu'une rangée et masque des chiffres:
// A 1024-1279 px (1 pastille gardée), B 1280-1365 px (2), C 1366-1439 px (3).
// Classes complètes (Tailwind ne devine pas les classes composées à la volée).
const PLAGES = [
  {
    gardees: PASTILLES_ETROIT,
    cache: 'lg:max-[1279px]:hidden',
    inline: 'lg:max-[1279px]:inline-flex',
    flex: 'lg:max-[1279px]:flex',
  },
  {
    gardees: PASTILLES_MOYEN,
    cache: 'min-[1280px]:max-[1365px]:hidden',
    inline: 'min-[1280px]:max-[1365px]:inline-flex',
    flex: 'min-[1280px]:max-[1365px]:flex',
  },
  {
    gardees: PASTILLES_LARGE,
    cache: 'min-[1366px]:max-[1439px]:hidden',
    inline: 'min-[1366px]:max-[1439px]:inline-flex',
    flex: 'min-[1366px]:max-[1439px]:flex',
  },
];

/**
 * Disposition « rangees » (en-têtes illustrés, PageHero): toutes les pastilles
 * restent visibles à toute largeur (rien ne part dans « Plus » faute de place),
 * rangées en grille régulière sans pastille seule sur sa rangée:
 *  - 1 case: seule; 2 ou 3 cases: une rangée (dès 640 px);
 *  - 4 cases: 2×2; 5 cases: 3+2; 6 cases: 3+3 (deux rangées, en colonnes);
 *  - sous 640 px: deux colonnes, la dernière case impaire occupe toute la
 *    largeur de sa rangée; sous 360 px, à partir de quatre cases, une seule
 *    colonne (les libellés ne sont pas coupés).
 */
export const formeRangees = (cases) => {
  if (cases <= 1) return { colonnes: 1, rangees: 1 };
  if (cases <= 3) return { colonnes: cases, rangees: 1 };
  return { colonnes: Math.ceil(cases / 2), rangees: 2 };
};

/**
 * Pour chaque plage: combien de chiffres sont gardés dans l'en-tête. Règle
 * anti-orphelin: « Plus » n'apparaît jamais pour UN SEUL chiffre masqué (sans
 * contenu `plus`): ce chiffre reste alors affiché à sa place.
 */
export const gardeesParPlage = (total, aPlusContenu) =>
  PLAGES.map(({ gardees }) => {
    const masques = total - gardees;
    if (masques <= 0) return total;
    if (masques === 1 && !aPlusContenu) return total;
    return gardees;
  });

/** Plages (indices) où le chiffre de ce rang quitte l'en-tête. */
const plagesMasquees = (rang, gardees) =>
  PLAGES.map((_, i) => i).filter((i) => rang >= gardees[i]);

/**
 * Les chiffres tiennent-ils sur DEUX rangées dès 1440 px? Sert à décider si les
 * boutons empilés et les alertes en deux rangées s'alignent sur eux.
 */
export const chiffresSurDeuxRangees = ({ stats, plus, disposition = 'grille' } = {}) => {
  const n = Array.isArray(stats) ? Math.min(stats.length, 4) : 0;
  return disposition !== 'ligne' && n + (plus ? 1 : 0) > 1;
};

function Puce({ stat, masquee = [], className }) {
  const { label, valeur, unite, ton = 'neutre', aide, onClick, testid } = stat;
  const interactive = typeof onClick === 'function';
  const Composant = interactive ? 'button' : 'div';
  const abregee = valeurCompacte(stat);
  const exacte = abregee ? affiche(valeur) : null;
  const titre = [aide, exacte].filter(Boolean).join(' — ') || undefined;
  return (
    <Composant
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      title={titre}
      data-testid={testid}
      className={cn(GABARIT, interactive && INTERACTIF, masquee.map((i) => PLAGES[i].cache), className)}
    >
      <span
        className={cn(
          'text-sm font-semibold tabular-nums',
          TONS[ton] || TONS.neutre,
        )}
      >
        {abregee ? (
          <>
            <span className="max-[1439px]:hidden">{affiche(valeur)}</span>
            <span className="min-[1440px]:hidden" aria-hidden="true">
              {abregee}
            </span>
          </>
        ) : (
          affiche(valeur)
        )}
        {unite ? (
          <span className="ml-1 text-xs font-medium text-muted-foreground">
            {unite}
          </span>
        ) : null}
      </span>
      <span className="truncate text-xs text-muted-foreground">{label}</span>
    </Composant>
  );
}

const StatsCompactes = ({
  stats,
  plus,
  plusLabel = 'Plus d’indicateurs',
  disposition = 'grille',
  className,
}) => {
  // Les en-têtes illustrés (« rangees ») acceptent jusqu'à six chiffres.
  const liste = Array.isArray(stats) ? stats.slice(0, disposition === 'rangees' ? 6 : 4) : [];
  if (liste.length === 0 && !plus) return null;
  const rangs = rangsDePriorite(liste);
  const rangeesEntete = disposition === 'rangees';
  const enPile = disposition === 'pile' && liste.length + (plus ? 1 : 0) > 1;
  // « libre »: toutes visibles à toute largeur, à la ligne quand la place manque.
  const libre = disposition === 'libre';
  const gardees = rangeesEntete || enPile || libre
    ? PLAGES.map(() => liste.length)
    : gardeesParPlage(liste.length, Boolean(plus));
  // Plages où chaque chiffre quitte l'en-tête (il passe alors dans « Plus »).
  const masquees = liste.map((_, index) => plagesMasquees(rangs[index], gardees));
  const reportees = liste
    .map((stat, index) => ({ stat, rang: rangs[index], plages: masquees[index] }))
    .filter(({ plages }) => plages.length > 0)
    .sort((a, b) => a.rang - b.rang);
  // Plages où la pastille « Plus » existe sans contenu `plus`.
  const plagesPlus = PLAGES.map((_, i) => i).filter((i) =>
    liste.some((_s, index) => masquees[index].includes(i)),
  );
  // Grille: une colonne pour une seule case, deux colonnes au-delà. Quatre
  // cases (la pastille « Plus d'indicateurs » compte pour une) tiennent en 2×2.
  const cases = liste.length + (plus ? 1 : 0);
  // Disposition « ligne »: toutes les pastilles côte à côte sur une seule
  // rangée; sous 1024 px la rangée défile au lieu de passer à la ligne.
  const enLigne = disposition === 'ligne' && cases > 1;
  const aPlus = Boolean(plus) || plagesPlus.length > 0;
  // Anti-orphelin dès 1440 px (deux rangées): un nombre impair de cases laisse
  // une case seule dans sa colonne. Avec « Plus », il devient une petite
  // pastille collée au dernier chiffre; sans « Plus », le dernier chiffre
  // s'étend sur les deux rangées.
  const impair = !enLigne && !enPile && !rangeesEntete && !libre && cases > 2 && cases % 2 === 1;
  const fusionPlus = impair && Boolean(plus) && liste.length >= 2;
  const dernierEtendu = impair && !plus && liste.length >= 3;

  const bouton = aPlus ? (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="stats-plus"
          className={cn(
            GABARIT,
            INTERACTIF,
            'justify-between text-xs font-medium text-muted-foreground hover:text-foreground',
            fusionPlus && 'min-[1440px]:w-7 min-[1440px]:justify-center min-[1440px]:px-0',
            // Sans contenu `plus`, la pastille n'existe que là où des
            // chiffres ont quitté l'en-tête.
            !plus && ['hidden', plagesPlus.map((i) => PLAGES[i].inline)],
          )}
        >
          <span
            className={cn('truncate', fusionPlus && 'min-[1440px]:hidden')}
            title={plusLabel}
            aria-label={plusLabel}
          >
            <span className="2xl:hidden" aria-hidden="true">Plus</span>
            <span className="hidden 2xl:inline">{plusLabel}</span>
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)]">
        {reportees.length > 0 ? (
          <dl
            data-testid="stats-reportees"
            className={cn(
              'hidden flex-col gap-2 text-sm',
              plagesPlus.map((i) => PLAGES[i].flex),
              plus && 'mb-3 border-b border-border/60 pb-3',
            )}
          >
            {reportees.map(({ stat, plages }) => (
              <div
                key={stat.label}
                className={cn(
                  'hidden items-baseline justify-between gap-3',
                  plages.map((i) => PLAGES[i].flex),
                )}
              >
                <dt className="text-muted-foreground">{stat.label}</dt>
                <dd
                  className={cn(
                    'font-bold tabular-nums',
                    TONS[stat.ton] || TONS.neutre,
                  )}
                >
                  {affiche(stat.valeur)}
                  {stat.unite ? ` ${stat.unite}` : ''}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {plus}
      </PopoverContent>
    </Popover>
  ) : null;

  const puces = liste.map((stat, index) => (
    <Puce
      key={stat.label}
      stat={stat}
      masquee={masquees[index]}
      className={
        dernierEtendu && index === liste.length - 1
          ? 'min-[1440px]:row-span-2 min-[1440px]:self-center'
          : undefined
      }
    />
  ));

  return (
    <div
      className={cn(
        rangeesEntete
          ? cn(
              'min-w-0 max-w-full gap-1.5 max-sm:w-full [&>*]:min-w-0',
              cases <= 1 && 'grid grid-cols-1',
              // Deux ou trois cases: côte à côte dès 640 px; dessous, deux
              // colonnes, la dernière impaire sur toute la largeur.
              (cases === 2 || cases === 3) &&
                'grid grid-cols-2 [&>:last-child:nth-child(odd)]:col-span-2 sm:flex sm:flex-nowrap sm:items-center sm:[&>:last-child:nth-child(odd)]:col-span-1',
              // Quatre cases: 2×2 partout.
              cases === 4 && 'grid grid-cols-2 max-[359px]:grid-cols-1',
              // Cinq cases et plus: deux rangées en colonnes dès 640 px
              // (5 = 3+2, 6 = 3+3); dessous, deux colonnes.
              cases >= 5 &&
                'grid grid-cols-2 max-[359px]:grid-cols-1 [&>:last-child:nth-child(odd)]:col-span-2 max-[359px]:[&>:last-child:nth-child(odd)]:col-span-1 sm:grid-flow-col sm:grid-cols-none sm:grid-rows-2 sm:auto-cols-max sm:[&>:last-child:nth-child(odd)]:col-span-1',
            )
          : libre
          ? 'flex min-w-0 max-w-full flex-wrap items-center gap-1.5'
          : enPile
          ? // Colonne dès 1024 px; dessous, la rangée qui défile.
            'flex min-w-0 max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto [scrollbar-width:none] lg:flex-col lg:items-stretch lg:overflow-visible'
          : enLigne
          ? 'flex min-w-0 max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto [scrollbar-width:none] lg:overflow-visible'
          : cases > 1
            ? // Deux rangées au plus à partir de 1440 px (A C E / B D); en
              // dessous, une seule rangée qui défile sous 1024 px.
              'flex min-w-0 max-w-full items-center gap-1.5 overflow-x-auto [scrollbar-width:none] lg:overflow-visible min-[1440px]:grid min-[1440px]:grid-flow-col min-[1440px]:grid-rows-2 min-[1440px]:auto-cols-max'
            : 'grid min-w-0 grid-cols-1 gap-1.5',
        className,
      )}
      data-disposition={rangeesEntete ? 'rangees' : libre ? 'libre' : enPile ? 'pile' : enLigne ? 'ligne' : 'grille'}
      data-cases={rangeesEntete ? cases : undefined}
      data-forme={rangeesEntete ? `${formeRangees(cases).colonnes}x${formeRangees(cases).rangees}` : undefined}
      data-fusion-plus={fusionPlus ? '' : undefined}
      role="group"
      aria-label="Chiffres essentiels"
    >
      {fusionPlus ? (
        <>
          {puces.slice(0, -1)}
          <div className="contents min-[1440px]:flex min-[1440px]:items-center min-[1440px]:gap-1.5">
            {puces[puces.length - 1]}
            {bouton}
          </div>
        </>
      ) : (
        <>
          {puces}
          {bouton}
        </>
      )}
    </div>
  );
};
StatsCompactes.displayName = 'StatsCompactes';

export { StatsCompactes };
